import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { visaApi } from "@/lib/visa";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api";
import { Plus, Star, Trash2 } from "lucide-react";

const emptyTpl = () => ({
  name: "New Template",
  logo_text: "AIMS Healthcare",
  sub_heading: "International Patient Management",
  title: "MEDICAL VISA INVITATION LETTER",
  greeting: "To Whom It May Concern,",
  body: "This letter invites {{patient_name}} (Passport {{passport_number}}, {{nationality}}) to travel for treatment at {{hospital_name}}.",
  footer: "This invitation is issued in good faith for medical purposes only.",
  authorised_signatory: "Dr. Authorised Signatory\nMedical Superintendent",
  contact_details: "AIMS Healthcare · contact@aimshealthcare.in",
  hospital_address: "AIMS Healthcare International Patient Desk, New Delhi",
  is_default: false,
});

export default function VisaTemplates() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setItems(await visaApi.listTemplates()); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const t = await visaApi.createTemplate(emptyTpl());
      await load();
      setSelected(t);
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const upd = await visaApi.updateTemplate(selected.id, {
        name: selected.name, logo_text: selected.logo_text, sub_heading: selected.sub_heading,
        title: selected.title, greeting: selected.greeting, body: selected.body,
        footer: selected.footer, authorised_signatory: selected.authorised_signatory,
        contact_details: selected.contact_details, hospital_address: selected.hospital_address,
        is_default: selected.is_default,
      });
      setSelected(upd);
      await load();
      toast.success("Saved");
    } catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this template?")) return;
    try { await visaApi.deleteTemplate(id); await load(); if (selected?.id === id) setSelected(null); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const setField = (f, v) => setSelected((s) => ({ ...s, [f]: v }));
  const input = "w-full border border-[#E8E6E1] rounded-lg px-3 py-2 text-sm focus:border-[#2A4D3E] outline-none";

  return (
    <div data-testid="visa-templates-page">
      <PageHeader eyebrow="International Services · Admin" title="Invitation Letter Templates"
        subtitle="Design DB-stored templates for the visa PDF generator. Placeholders like {{patient_name}} auto-substitute."
        actions={<button onClick={create} className="bg-[#2A4D3E] text-white rounded-full px-5 py-2 text-sm font-medium flex items-center gap-2" data-testid="new-template-btn"><Plus size={14} /> New Template</button>}
      />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-3 lg:col-span-1">
          {loading ? <div className="text-sm text-[#5C6660] p-3">Loading…</div> : items.length === 0 ? (
            <div className="text-sm text-[#8E9992] text-center py-6">No templates yet.</div>
          ) : items.map((t) => (
            <button key={t.id} onClick={() => setSelected(t)}
              className={`w-full text-left px-3 py-2 rounded-lg mb-1 flex items-center justify-between ${selected?.id === t.id ? "bg-[#E6F0EB]" : "hover:bg-[#FAFAF7]"}`}
              data-testid={`template-${t.id}`}>
              <div>
                <div className="text-sm font-medium text-[#2A332C] flex items-center gap-1">{t.is_default && <Star size={12} className="text-[#D97757] fill-current" />} {t.name}</div>
                <div className="text-[11px] text-[#8E9992] mt-0.5">{t.title}</div>
              </div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-3">
          {!selected ? (
            <div className="bg-white border border-[#E8E6E1] rounded-2xl p-12 text-center text-sm text-[#8E9992]">
              Select a template from the left to edit, or create a new one.
            </div>
          ) : (
            <div className="bg-white border border-[#E8E6E1] rounded-2xl p-5 space-y-4" data-testid="template-editor">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label><div className="text-[11px] font-semibold text-[#8E9992] uppercase mb-1">Name</div><input className={input} value={selected.name} onChange={(e) => setField("name", e.target.value)} data-testid="tpl-name" /></label>
                <label><div className="text-[11px] font-semibold text-[#8E9992] uppercase mb-1">Logo text</div><input className={input} value={selected.logo_text || ""} onChange={(e) => setField("logo_text", e.target.value)} /></label>
                <label><div className="text-[11px] font-semibold text-[#8E9992] uppercase mb-1">Sub heading</div><input className={input} value={selected.sub_heading || ""} onChange={(e) => setField("sub_heading", e.target.value)} /></label>
                <label><div className="text-[11px] font-semibold text-[#8E9992] uppercase mb-1">Letter title</div><input className={input} value={selected.title || ""} onChange={(e) => setField("title", e.target.value)} /></label>
                <label className="md:col-span-2"><div className="text-[11px] font-semibold text-[#8E9992] uppercase mb-1">Greeting</div><input className={input} value={selected.greeting || ""} onChange={(e) => setField("greeting", e.target.value)} /></label>
              </div>
              <label className="block">
                <div className="text-[11px] font-semibold text-[#8E9992] uppercase mb-1">Body (supports HTML tags &lt;b&gt;, &lt;br/&gt; and placeholders like {"{{patient_name}}"})</div>
                <textarea className={`${input} min-h-40`} value={selected.body} onChange={(e) => setField("body", e.target.value)} data-testid="tpl-body" />
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label><div className="text-[11px] font-semibold text-[#8E9992] uppercase mb-1">Footer</div><input className={input} value={selected.footer || ""} onChange={(e) => setField("footer", e.target.value)} /></label>
                <label><div className="text-[11px] font-semibold text-[#8E9992] uppercase mb-1">Hospital address</div><input className={input} value={selected.hospital_address || ""} onChange={(e) => setField("hospital_address", e.target.value)} /></label>
                <label><div className="text-[11px] font-semibold text-[#8E9992] uppercase mb-1">Authorised signatory (multi-line)</div><textarea className={`${input} min-h-20`} value={selected.authorised_signatory || ""} onChange={(e) => setField("authorised_signatory", e.target.value)} /></label>
                <label><div className="text-[11px] font-semibold text-[#8E9992] uppercase mb-1">Contact details</div><textarea className={`${input} min-h-20`} value={selected.contact_details || ""} onChange={(e) => setField("contact_details", e.target.value)} /></label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={selected.is_default || false} onChange={(e) => setField("is_default", e.target.checked)} className="w-4 h-4" data-testid="tpl-default" />
                Mark as default template
              </label>
              <div className="flex items-center justify-between pt-2 border-t border-[#E8E6E1]">
                <button onClick={() => del(selected.id)} className="text-[#C0392B] text-sm flex items-center gap-1.5" data-testid="tpl-delete"><Trash2 size={14} /> Delete</button>
                <button onClick={save} disabled={saving} className="bg-[#2A4D3E] text-white rounded-full px-6 py-2 text-sm font-semibold" data-testid="tpl-save">{saving ? "Saving…" : "Save changes"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
